import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";
import { useMeCore } from "../../../cores/me";
import { useEmailMarketingCore } from "../../../cores/emailMarketing";
import Content from "../../layout/Content";
import Layout from "../../layout/Layout";
import { EmailMktModal } from "./shared";
import "./EmailMarketing.scss";

export const EmailMarketingConfigScreen = () => {
  const intl = useIntl();
  const { company } = useMeCore();
  const {
    config,
    listSetting,
    loadConfig,
    loadList,
    updateList,
    createSender,
    createUnsubscribeGroup,
    loadDomainDns,
    setupDomain,
    repairMarketingSetup,
  } = useEmailMarketingCore();
  const [repairingMarketing, setRepairingMarketing] = useState(false);
  const [senders, setSenders] = useState(config?.senders || []);
  const [groups, setGroups] = useState(config?.unsubscribeGroups || []);
  const [defaultSenderEmail, setDefaultSenderEmail] = useState("");
  const [defaultSenderName, setDefaultSenderName] = useState("");
  const [domainInput, setDomainInput] = useState("");
  const [senderModalOpen, setSenderModalOpen] = useState(false);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [dnsModalOpen, setDnsModalOpen] = useState(false);
  const [dnsRecords, setDnsRecords] = useState<
    { type: string; host: string; value: string; valid?: boolean }[]
  >([]);
  const [newSenderEmail, setNewSenderEmail] = useState("");
  const [newSenderName, setNewSenderName] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupDesc, setNewGroupDesc] = useState("");

  useEffect(() => {
    if (company?.id) {
      loadConfig(company.id);
      loadList(company.id);
    }
  }, [company?.id, loadConfig, loadList]);

  useEffect(() => {
    if (config) {
      setSenders(config.senders);
      setGroups(config.unsubscribeGroups);
    }
  }, [config]);

  useEffect(() => {
    if (listSetting?.fromEmail) {
      setDefaultSenderEmail(listSetting.fromEmail);
      setDefaultSenderName(listSetting.fromName || listSetting.fromEmail);
      return;
    }
    if (config?.activeSender?.email) {
      setDefaultSenderEmail(config.activeSender.email);
      setDefaultSenderName(config.activeSender.name);
    }
  }, [listSetting, config?.activeSender]);

  useEffect(() => {
    if (config?.domains?.[0]?.domain) {
      setDomainInput(config.domains[0].domain);
    }
  }, [config?.domains]);

  const saveSender = async () => {
    if (!newSenderEmail.trim() || !company?.id) {
      return;
    }
    const sender = await createSender(company.id, {
      email: newSenderEmail.trim(),
      name: newSenderName.trim() || newSenderEmail.trim(),
    });
    if (sender) {
      setSenders((current) => [...current, sender]);
      setSenderModalOpen(false);
      setNewSenderEmail("");
      setNewSenderName("");
    }
  };

  const saveGroup = async () => {
    if (!newGroupName.trim() || !company?.id) {
      return;
    }
    const group = await createUnsubscribeGroup(company.id, {
      name: newGroupName.trim(),
      description: newGroupDesc.trim(),
    });
    if (group) {
      setGroups((current) => [...current, group]);
      setGroupModalOpen(false);
      setNewGroupName("");
      setNewGroupDesc("");
    }
  };

  const saveDefaultSender = async () => {
    if (!company?.id || !defaultSenderEmail.trim()) {
      toast.info(intl.formatMessage({ id: "app.emailMarketing.api.senderRequired" }));
      return;
    }
    await updateList(company.id, {
      fromEmail: defaultSenderEmail.trim(),
      fromName: defaultSenderName.trim() || defaultSenderEmail.trim(),
    });
  };

  const openDnsModal = async () => {
    if (!company?.id) {
      return;
    }
    const dns = await loadDomainDns(company.id);
    if (dns) {
      setDnsRecords(dns.records);
      setDnsModalOpen(true);
      if (!dns.records.length) {
        toast.info(intl.formatMessage({ id: "app.emailMarketing.api.noDns" }));
      }
    }
  };

  const saveDomain = async () => {
    if (!company?.id || !domainInput.trim()) {
      toast.info(intl.formatMessage({ id: "app.emailMarketing.config.domainRequired" }));
      return;
    }
    const dns = await setupDomain(company.id, domainInput.trim());
    if (dns) {
      setDnsRecords(dns.records);
      if (dns.records.length) {
        setDnsModalOpen(true);
      }
    }
  };

  const domainLabel = config?.domains?.[0]?.domain;
  const domainAuthenticated = config?.domains?.[0]?.authenticated ?? false;
  const delivery = config?.delivery;

  const handleRepairMarketing = async () => {
    if (!company?.id) {
      return;
    }
    setRepairingMarketing(true);
    await repairMarketingSetup(company.id);
    setRepairingMarketing(false);
  };

  return (
    <Layout>
      <Content className="email-mkt email-mkt--config">
        <section className="email-mkt__reports-heading">
          <div>
            <span className="email-mkt__eyebrow">
              <FormattedMessage id="app.sidebar.emailMarketing" />
            </span>
            <h2>
              <FormattedMessage id="app.emailMarketing.config.title" />
            </h2>
            <p>
              <FormattedMessage id="app.emailMarketing.config.subtitle" />
            </p>
          </div>
        </section>

        {delivery && (
          <section
            className={`email-mkt__delivery-banner ${
              delivery.readyForBulk ? "is-ok" : "is-warn"
            }`}
          >
            <div>
              <strong>
                <FormattedMessage
                  id={
                    delivery.readyForBulk
                      ? "app.emailMarketing.delivery.titleReady"
                      : "app.emailMarketing.delivery.titleNotReady"
                  }
                />
              </strong>
              <p className="email-mkt__copy">
                <FormattedMessage
                  id={`app.emailMarketing.delivery.mode.${delivery.mode}`}
                />
                {delivery.checks.listContactCount > 0 && (
                  <>
                    {" "}
                    · {delivery.checks.listContactCount}{" "}
                    <FormattedMessage id="app.emailMarketing.delivery.contactsInList" />
                  </>
                )}
                {!delivery.readyForBulk && (
                  <>
                    {" "}
                    ·{" "}
                    <FormattedMessage
                      id="app.emailMarketing.delivery.localLimit"
                      values={{ max: delivery.maxRecipientsWithoutMarketing }}
                    />
                  </>
                )}
              </p>
              {delivery.issues.length > 0 && (
                <ul className="email-mkt__delivery-issues">
                  {delivery.issues.map((issue) => (
                    <li key={issue.code}>{issue.message}</li>
                  ))}
                </ul>
              )}
            </div>
            {!delivery.readyForBulk && (
              <button
                type="button"
                className="email-mkt__primary-btn"
                disabled={repairingMarketing}
                onClick={handleRepairMarketing}
              >
                {repairingMarketing
                  ? intl.formatMessage({
                      id: "app.emailMarketing.delivery.repairing",
                    })
                  : intl.formatMessage({
                      id: "app.emailMarketing.delivery.repair",
                    })}
              </button>
            )}
          </section>
        )}

        <section className="email-mkt__section">
          <div className="email-mkt__section-title">
            <span className="email-mkt__section-icon">01</span>
            <h2>
              <FormattedMessage id="app.emailMarketing.config.engineTitle" />
            </h2>
          </div>
          <div className="email-mkt__engine-grid">
            <div className="email-mkt__engine-option is-active">
              <strong>
                <FormattedMessage id="app.emailMarketing.engine.name" />
              </strong>
              <span>
                <FormattedMessage id="app.emailMarketing.config.engineActive" />
              </span>
            </div>
          </div>
          <p className="email-mkt__copy">
            <FormattedMessage id="app.emailMarketing.config.engineHint" />
          </p>
        </section>

        <section className="email-mkt__section">
          <div className="email-mkt__section-title">
            <span className="email-mkt__section-icon">02</span>
            <h2>
              <FormattedMessage id="app.emailMarketing.config.sendersTitle" />
            </h2>
          </div>
          <div className="email-mkt__table">
            <div className="email-mkt__table-row email-mkt__table-row--head">
              <span>
                <FormattedMessage id="app.emailMarketing.lists.email" />
              </span>
              <span>
                <FormattedMessage id="app.emailMarketing.lists.name" />
              </span>
              <span>
                <FormattedMessage id="app.emailMarketing.config.status" />
              </span>
            </div>
            {senders.map((sender) => (
              <div className="email-mkt__table-row" key={sender.email}>
                <strong>{sender.email}</strong>
                <span>{sender.name}</span>
                <span
                  className={
                    sender.verified
                      ? "email-mkt__pill email-mkt__pill--ok"
                      : "email-mkt__pill"
                  }
                >
                  <FormattedMessage
                    id={
                      sender.verified
                        ? "app.emailMarketing.config.verified"
                        : "app.emailMarketing.config.pending"
                    }
                  />
                </span>
              </div>
            ))}
          </div>
          <button
            type="button"
            className="email-mkt__ghost-btn email-mkt__mt-sm"
            onClick={() => setSenderModalOpen(true)}
          >
            <FormattedMessage id="app.emailMarketing.config.addSender" />
          </button>
        </section>

        <section className="email-mkt__section">
          <div className="email-mkt__section-title">
            <span className="email-mkt__section-icon">Def</span>
            <h2>
              <FormattedMessage id="app.emailMarketing.config.companySenderTitle" />
            </h2>
          </div>
          <p className="email-mkt__copy">
            <FormattedMessage id="app.emailMarketing.config.companySenderHelp" />
          </p>
          <div className="email-mkt__form-grid">
            <label className="email-mkt__field">
              <span>
                <FormattedMessage id="app.emailMarketing.lists.fromAddress" />
              </span>
              <select
                value={defaultSenderEmail}
                onChange={(event) => {
                  const nextEmail = event.target.value;
                  setDefaultSenderEmail(nextEmail);
                  const selected = senders.find((sender) => sender.email === nextEmail);
                  if (selected?.name) {
                    setDefaultSenderName(selected.name);
                  }
                }}
              >
                <option value="">
                  {intl.formatMessage({ id: "app.emailMarketing.config.selectSender" })}
                </option>
                {senders.map((sender) => (
                  <option key={sender.email} value={sender.email}>
                    {sender.email}
                  </option>
                ))}
              </select>
            </label>
            <label className="email-mkt__field">
              <span>
                <FormattedMessage id="app.emailMarketing.lists.fromName" />
              </span>
              <input
                value={defaultSenderName}
                onChange={(event) => setDefaultSenderName(event.target.value)}
              />
            </label>
          </div>
          <button
            type="button"
            className="email-mkt__primary-btn email-mkt__mt-sm"
            onClick={saveDefaultSender}
          >
            <FormattedMessage id="app.emailMarketing.lists.saveSender" />
          </button>
        </section>

        <section className="email-mkt__section">
          <div className="email-mkt__section-title">
            <span className="email-mkt__section-icon">03</span>
            <h2>
              <FormattedMessage id="app.emailMarketing.config.domainTitle" />
            </h2>
          </div>
          <p className="email-mkt__copy">
            <FormattedMessage id="app.emailMarketing.config.domainHelp" />
          </p>
          {domainLabel ? (
            <div className="email-mkt__domain-status">
              <span
                className={
                  domainAuthenticated
                    ? "email-mkt__pill email-mkt__pill--ok"
                    : "email-mkt__pill"
                }
              >
                {domainLabel}
              </span>
              <span>
                {domainAuthenticated ? (
                  <FormattedMessage id="app.emailMarketing.config.domainAuthenticated" />
                ) : (
                  <FormattedMessage id="app.emailMarketing.config.pending" />
                )}
              </span>
            </div>
          ) : (
            <p className="email-mkt__note">
              <FormattedMessage id="app.emailMarketing.config.noDomain" />
            </p>
          )}
          <div className="email-mkt__form-grid email-mkt__mt-sm">
            <label className="email-mkt__field">
              <span>
                <FormattedMessage id="app.emailMarketing.config.domainInput" />
              </span>
              <input
                value={domainInput}
                onChange={(event) => setDomainInput(event.target.value)}
                placeholder="empresa.com"
              />
            </label>
          </div>
          <div className="email-mkt__create-btn-group email-mkt__mt-sm">
            <button
              type="button"
              className="email-mkt__primary-btn"
              onClick={saveDomain}
            >
              <FormattedMessage id="app.emailMarketing.config.setupDomain" />
            </button>
            {domainLabel && (
              <button
                type="button"
                className="email-mkt__ghost-btn"
                onClick={openDnsModal}
              >
                <FormattedMessage id="app.emailMarketing.config.viewDns" />
              </button>
            )}
          </div>
        </section>

        <section className="email-mkt__section">
          <div className="email-mkt__section-title">
            <span className="email-mkt__section-icon">04</span>
            <h2>
              <FormattedMessage id="app.emailMarketing.config.unsubscribeTitle" />
            </h2>
          </div>
          <div className="email-mkt__table email-mkt__table--actions">
            <div className="email-mkt__table-row email-mkt__table-row--head">
              <span>
                <FormattedMessage id="app.emailMarketing.config.groupName" />
              </span>
              <span>
                <FormattedMessage id="app.emailMarketing.lists.description" />
              </span>
              <span>
                <FormattedMessage id="app.emailMarketing.lists.subscribers" />
              </span>
              <span />
            </div>
            {groups.map((group) => (
              <div className="email-mkt__table-row" key={group.id}>
                <strong>{group.name}</strong>
                <span>{group.description}</span>
                <span>{group.count}</span>
                <button type="button" aria-label="Edit" onClick={() => setGroupModalOpen(true)}>
                  ⋮
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            className="email-mkt__ghost-btn email-mkt__mt-sm"
            onClick={() => setGroupModalOpen(true)}
          >
            <FormattedMessage id="app.emailMarketing.config.addGroup" />
          </button>
        </section>

        <section className="email-mkt__section">
          <div className="email-mkt__section-title">
            <span className="email-mkt__section-icon">05</span>
            <h2>
              <FormattedMessage id="app.emailMarketing.config.addressTitle" />
            </h2>
          </div>
          <p className="email-mkt__copy">
            <FormattedMessage id="app.emailMarketing.config.addressHelp" />
          </p>
          <address className="email-mkt__address-block">
            2445 San Pío X, Providencia
            <br />
            Región Metropolitana 7510602, Chile
          </address>
        </section>

        <EmailMktModal
          open={senderModalOpen}
          title={<FormattedMessage id="app.emailMarketing.config.addSender" />}
          onClose={() => setSenderModalOpen(false)}
          footer={
            <button type="button" className="email-mkt__primary-btn" onClick={saveSender}>
              <FormattedMessage id="app.emailMarketing.lists.add" />
            </button>
          }
        >
          <label className="email-mkt__field">
            <span>
              <FormattedMessage id="app.emailMarketing.lists.email" />
            </span>
            <input
              value={newSenderEmail}
              onChange={(event) => setNewSenderEmail(event.target.value)}
              placeholder="marketing@empresa.com"
            />
          </label>
          <label className="email-mkt__field">
            <span>
              <FormattedMessage id="app.emailMarketing.lists.name" />
            </span>
            <input
              value={newSenderName}
              onChange={(event) => setNewSenderName(event.target.value)}
            />
          </label>
        </EmailMktModal>

        <EmailMktModal
          open={groupModalOpen}
          title={<FormattedMessage id="app.emailMarketing.config.addGroup" />}
          onClose={() => setGroupModalOpen(false)}
          footer={
            <button type="button" className="email-mkt__primary-btn" onClick={saveGroup}>
              <FormattedMessage id="app.emailMarketing.lists.add" />
            </button>
          }
        >
          <label className="email-mkt__field">
            <span>
              <FormattedMessage id="app.emailMarketing.config.groupName" />
            </span>
            <input
              value={newGroupName}
              onChange={(event) => setNewGroupName(event.target.value)}
            />
          </label>
          <label className="email-mkt__field">
            <span>
              <FormattedMessage id="app.emailMarketing.lists.description" />
            </span>
            <textarea
              value={newGroupDesc}
              onChange={(event) => setNewGroupDesc(event.target.value)}
              rows={3}
            />
          </label>
        </EmailMktModal>

        <EmailMktModal
          open={dnsModalOpen}
          title={<FormattedMessage id="app.emailMarketing.config.viewDns" />}
          onClose={() => setDnsModalOpen(false)}
          footer={
            <button
              type="button"
              className="email-mkt__primary-btn"
              onClick={() => setDnsModalOpen(false)}
            >
              <FormattedMessage id="app.emailMarketing.emails.wizard.close" />
            </button>
          }
        >
          {dnsRecords.length === 0 ? (
            <p className="email-mkt__copy">
              <FormattedMessage id="app.emailMarketing.api.noDns" />
            </p>
          ) : (
            <div className="email-mkt__table">
              <div className="email-mkt__table-row email-mkt__table-row--head">
                <span>Type</span>
                <span>Host</span>
                <span>Value</span>
              </div>
              {dnsRecords.map((record) => (
                <div className="email-mkt__table-row" key={`${record.type}-${record.host}`}>
                  <strong>{record.type}</strong>
                  <span>{record.host}</span>
                  <span>{record.value}</span>
                </div>
              ))}
            </div>
          )}
        </EmailMktModal>
      </Content>
    </Layout>
  );
};
